import type React from "react";
import { Button } from "@planta-v2/pds-ui/button";
import { i18n } from "@frontend/i18n";
import type { DirectoryCard } from "@frontend/shared/domain/types/directory-card.types";
import { EventParticipantIdPublic } from "@shared/schemas/branded-id.schemas";
import { DirectoryListSkeleton } from "../state/DirectoryListSkeleton";
import { ProfileList } from "./ProfileList";
import { ProfileListItem } from "./ProfileListItem";

// Fixed, not fitted to the screen: a limit that waits on the other group or the
// keyboard makes the list shrink and grow under the reader's finger.
const SEARCH_GROUP_ROW_LIMIT = 3;

interface DirectorySearchGroupProps {
  readonly testId: string;
  readonly label: string;
  readonly entries: readonly DirectoryCard[];
  readonly isLoading: boolean;
  readonly loadingNote?: string | undefined;
  readonly searchQuery: string;
  readonly isExpanded: boolean;
  readonly onExpand: () => void;
  readonly isQueryMarked?: boolean;
  readonly isBioShown?: boolean;
  readonly currentUserParticipantIdPublic: string | null;
  readonly onProfileClick: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
}

export function DirectorySearchGroup({
  testId,
  label,
  entries,
  isLoading,
  loadingNote,
  searchQuery,
  isExpanded,
  onExpand,
  isQueryMarked = false,
  isBioShown = false,
  currentUserParticipantIdPublic,
  onProfileClick,
}: DirectorySearchGroupProps): React.JSX.Element {
  const visibleEntries = isExpanded
    ? entries
    : entries.slice(0, SEARCH_GROUP_ROW_LIMIT);

  return (
    <section data-testid={testId} aria-busy={isLoading}>
      <h2 className="pt-4 pb-1 text-pds-2xs font-semibold text-muted-foreground">
        {label}
      </h2>
      {isLoading ? (
        <>
          {loadingNote !== undefined && (
            <p
              data-testid={`${testId}-loading-note`}
              className="text-sm text-muted-foreground"
            >
              {loadingNote}
            </p>
          )}
          <DirectoryListSkeleton
            rowCount={SEARCH_GROUP_ROW_LIMIT}
            isCompact
            hasBioLine={isBioShown}
          />
        </>
      ) : (
        <ProfileList>
          {visibleEntries.map((entry) => (
            <ProfileListItem
              key={EventParticipantIdPublic.unwrap(entry.participantIdPublic)}
              entry={entry}
              onClick={onProfileClick}
              isCompact
              markedQuery={isQueryMarked ? searchQuery : undefined}
              isBioShown={isBioShown}
              isViewer={
                EventParticipantIdPublic.unwrap(entry.participantIdPublic) ===
                currentUserParticipantIdPublic
              }
            />
          ))}
        </ProfileList>
      )}
      {!isLoading && visibleEntries.length < entries.length && (
        <Button
          variant="link"
          size="sm"
          className="px-0"
          data-testid={`${testId}-see-all`}
          onClick={onExpand}
        >
          {i18n.t("user.directory.search.groups.seeAll", {
            count: entries.length,
          })}
        </Button>
      )}
    </section>
  );
}
